import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building, CheckCircle, Globe, TrendingUp, FileText, ClipboardCheck, Shield, Home, FileCheck, Landmark } from 'lucide-react';
import { Button } from '../components/ui/button';

const MainlandProcess = () => {
  const [activeStep, setActiveStep] = useState(1);

  const processSteps = [
    {
      number: 1,
      icon: ClipboardCheck,
      title: 'Business Activity Selection',
      description: 'Choose the appropriate business activity as per the Dubai Department of Economy & Tourism (DET/DED) approved list. The business activity determines the type of license required and any specific regulations that apply to your company. It\'s crucial to select activities that accurately reflect your business operations to ensure compliance.'
    },
    {
      number: 2,
      icon: Building,
      title: 'Legal Structure Finalization',
      description: 'Decide the legal form of the company (LLC, Sole Establishment, Civil Company, etc.) based on ownership and activity. LLCs are the most common for foreign investors as they allow up to 100% foreign ownership. The legal structure affects liability, ownership requirements, and operational flexibility.'
    },
    {
      number: 3,
      icon: FileText,
      title: 'Trade Name Reservation',
      description: 'Select and reserve a unique trade name that complies with DED naming guidelines. The name should not conflict with existing registered names, must not contain offensive words, and should reflect your business activity. We assist in checking name availability and ensuring compliance with all regulations.'
    },
    {
      number: 4,
      icon: CheckCircle,
      title: 'Initial Approval from DED',
      description: 'Obtain initial approval confirming that the authorities have no objection to starting the business. This approval is a preliminary clearance that allows you to proceed with the next steps of company formation. It verifies that your business activity and structure are acceptable under UAE regulations.'
    },
    {
      number: 5,
      icon: FileCheck,
      title: 'MOA / LSA Drafting (if applicable)',
      description: 'Prepare and notarize the Memorandum of Association (MOA) or Local Service Agent (LSA) agreement, if required. The MOA outlines company ownership, capital structure, and shareholder rights. For certain business activities, a Local Service Agent may be required, and we help facilitate this arrangement.'
    },
    {
      number: 6,
      icon: Home,
      title: 'Office Space & Ejari Registration',
      description: 'Lease a physical office or flexi-desk and register the tenancy contract through Ejari. DED requires proof of physical office space in Dubai. We can help you find suitable office solutions ranging from flexi-desks to dedicated offices, and handle the Ejari registration process for you.'
    },
    {
      number: 7,
      icon: Shield,
      title: 'External Approvals (if required)',
      description: 'Certain activities may require additional approvals from specific government authorities. This could include approvals from health authorities, civil defense, environmental agencies, or industry-specific regulators. We identify required approvals and manage the application process on your behalf.'
    },
    {
      number: 8,
      icon: Landmark,
      title: 'License Issuance & Payment',
      description: 'Submit final documents, pay government fees, and receive the DED Trade License. Once all approvals are obtained and documents are in order, we submit the final application and coordinate payment of all government fees. Upon approval, you receive your official DED Trade License.'
    },
    {
      number: 9,
      icon: Globe,
      title: 'Post-License Registrations',
      description: 'Complete immigration card, establishment card, visa processing, and corporate bank account opening. After license issuance, we assist with obtaining your immigration card and establishment card, processing investor and employee visas, and opening a corporate bank account with major UAE banks.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gray-800">Setup Process</span><br />
            <span className="text-gray-600">Step-by-Step Guide to Dubai Mainland Company Formation</span>
          </h2>
        </div>

        {/* Desktop View - Side by Side Layout */}
        <div className="hidden md:grid md:grid-cols-2 gap-8">
          {/* Left Side - Steps Selection */}
          <div className="space-y-3">
            {processSteps.map((step) => {
              const IconComponent = step.icon;
              const isActive = activeStep === step.number;
              return (
                <div
                  key={step.number}
                  className={`relative cursor-pointer transition-all duration-300 ${
                    isActive ? 'scale-102' : ''
                  }`}
                  onClick={() => setActiveStep(step.number)}
                  onMouseEnter={() => setActiveStep(step.number)}
                >
                  <div className={`flex items-center gap-4 p-4 rounded-xl ${
                    isActive 
                      ? 'bg-gradient-to-r from-[#8B1B4D] to-[#DC143C] shadow-xl' 
                      : 'bg-white shadow-md hover:shadow-lg'
                  } transition-all`}>
                    {/* Number Circle */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-bold text-2xl transition-all ${
                      isActive 
                        ? 'bg-white text-[#8B1B4D]' 
                        : 'bg-gradient-to-br from-[#8B1B4D] to-[#DC143C] text-white'
                    }`}>
                      {step.number}
                    </div>
                    {/* Icon */}
                    <div className={`p-2 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-white/20' 
                        : 'bg-gray-50'
                    }`}>
                      <IconComponent className={isActive ? 'text-white' : 'text-[#8B1B4D]'} size={28} />
                    </div>
                    {/* Step Title */}
                    <div className="flex-1">
                      <h4 className={`font-bold text-base leading-tight ${isActive ? 'text-white' : 'text-gray-800'}`}>
                        {step.title}
                      </h4>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Side - Active Step Detail */}
          <div className="sticky top-32 self-start">
            <div className="bg-white p-8 rounded-2xl shadow-2xl border-2 border-gray-100">
              <div className="flex items-start gap-5 mb-5">
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#8B1B4D] to-[#DC143C] rounded-full flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                  {activeStep}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">
                    {processSteps[activeStep - 1].title}
                  </h3>
                </div>
              </div>
              <p className="text-gray-700 text-base leading-relaxed">
                {processSteps[activeStep - 1].description}
              </p>
            </div>
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden">
          {/* Step Selector */}
          <div className="flex justify-center gap-2 mb-8 flex-wrap">
            {processSteps.map((step) => (
              <button
                key={step.number}
                onClick={() => setActiveStep(step.number)}
                className={`w-12 h-12 rounded-full font-bold text-lg transition-all shadow-lg ${
                  activeStep === step.number
                    ? 'bg-gradient-to-br from-[#8B1B4D] to-[#DC143C] text-white scale-110'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {step.number}
              </button>
            ))}
          </div>

          {/* Active Step Detail */}
          <div className="bg-white p-8 rounded-2xl shadow-2xl border-2 border-gray-100">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#8B1B4D] to-[#DC143C] rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                {activeStep}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {processSteps[activeStep - 1].title}
                </h3>
                <p className="text-gray-700 text-base leading-relaxed">
                  {processSteps[activeStep - 1].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Mainland = () => {
  const benefits = [
    'Conduct business anywhere in the UAE',
    'Trade with UAE mainland and globally',
    'No restrictions on office locations',
    'Ability to bid for government contracts',
    'Access to wider range of business activities',
    'Enhanced business credibility',
    'Potential for unlimited visa allocation',
    'No currency restrictions'
  ];

  const requirements = [
    'Valid passport copies of shareholders',
    'Proof of address (utility bill or bank statement)',
    'Business plan outlining activities',
    'Initial approval from DED',
    'Office lease agreement',
    'NOC from sponsor (if applicable)',
    'Company registration documents'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative py-32 flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(20, 10, 30, 0.8), rgba(20, 10, 30, 0.8)), url(https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <div className="flex items-center gap-3 mb-4">
              <Building size={48} className="text-[#DC143C]" />
              <span className="text-2xl font-bold">Mainland Company Setup</span>
            </div>
            <h1 className="text-5xl font-bold mb-6">Dubai Mainland Company Formation</h1>
            <p className="text-xl leading-relaxed mb-8">
              Set up your company in Dubai Mainland and enjoy the freedom to conduct business anywhere in the UAE with no restrictions on trade activities.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-[#DC143C] hover:bg-[#DC143C]/90 text-white px-8 py-6 text-lg"
              >
                Get Started Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">What is Dubai Mainland?</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Dubai Mainland companies are registered with the Department of Economic Development (DED) and operate under UAE federal law. Unlike free zone companies, mainland entities can trade directly with the UAE market and have no geographical restrictions on their operations.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              This setup is ideal for businesses that want to maximize their operational flexibility, engage in retail trade, or provide services across the emirate without limitations.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Key Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all">
                <CheckCircle className="text-green-600 mb-3" size={32} />
                <p className="text-gray-800 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Required Documents</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {requirements.map((req, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle className="text-[#6B1B4D] flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">{req}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <MainlandProcess />

      {/* Pricing Section */}
      <section className="py-20 bg-[#0A1929] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Starting from AED 15,000</h2>
          <p className="text-xl mb-8">All-inclusive package with license, visa processing, and documentation support</p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-[#DC143C] hover:bg-[#DC143C]/90 text-white px-8 py-6 text-lg"
            >
              Get Detailed Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Mainland;